import type { AppState } from '@auth0/auth0-react';

import { useAuth0, Auth0Provider } from '@auth0/auth0-react';
import { useMemo, useState, useEffect, useCallback } from 'react';

import axios from 'src/utils/axios';

import { CONFIG } from 'src/config-global';

import meService from 'src/services/me-service';

import { AuthContext } from '../auth-context';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { domain, clientId, callbackUrl } = CONFIG.auth0;

  const onRedirectCallback = useCallback((appState?: AppState) => {
    window.location.replace(appState?.returnTo || window.location.pathname);
  }, []);

  if (!(domain && clientId && callbackUrl)) {
    return null;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: callbackUrl,
        scope: 'api:access openid profile email',
        audience: CONFIG.auth0.audience,
      }}
      onRedirectCallback={onRedirectCallback}
    >
      <AuthProviderContainer>{children}</AuthProviderContainer>
    </Auth0Provider>
  );
}

// ----------------------------------------------------------------------

function AuthProviderContainer({ children }: Props) {
  const { user, isLoading, isAuthenticated, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);

  const fetchAccessToken = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const token = await getAccessTokenSilently();
        setAccessToken(token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Error fetching access token:', error);
      }
    } else {
      setAccessToken(null);
      delete axios.defaults.headers.common.Authorization;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const fetchIdTokenClaims = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const claims = await getIdTokenClaims();
        const userRoles = claims?.['https://api.workfin.co.uk/roles'] || [];
        const userTenantId = claims?.['https://api.workfin.co.uk/tenantId'] || null;
        const userTenantName = claims?.['https://api.workfin.co.uk/tenantName'] || null;
        // console.log('tnenantid', userTenantId);
        // console.log('tenantname', userTenantName);
        setRoles(userRoles);
        setTenantId(userTenantId);
        setTenantName(userTenantName);
      } catch (error) {
        console.error('Error fetching ID token claims:', error);
      }
    }
  }, [isAuthenticated, getIdTokenClaims]);

  useEffect(() => {
    const initializeAuth = async () => {
      await fetchAccessToken();
      await fetchIdTokenClaims();
      if (isAuthenticated) {
        try {
          await meService.logUserIn();
        } catch (error) {
          console.error('Post-login API call failed:', error);
        }
      }
    };

    initializeAuth();
  }, [fetchAccessToken, fetchIdTokenClaims, isAuthenticated]);

  const checkAuthenticated = isAuthenticated ? 'authenticated' : 'unauthenticated';
  const status = isLoading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: user
        ? {
          ...user,
          id: user?.sub,
          accessToken,
          displayName: user?.name,
          photoURL: user?.picture,
          roles,
          tenantId,
          tenantName
        }
        : null,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [accessToken, status, user, roles, tenantId, tenantName]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
